import React, { useState } from 'react';
import { Box, Chip, Menu, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NumbersIcon from '@mui/icons-material/Numbers';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

function formatDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? '—' : dt.toLocaleDateString();
}

export default function MetaChips({
  contentType,
  criticalityLevel,
  criticalityColor,     // fn(level) -> MUI color string
  updateNumber,
  issueDate,
  complianceDeadline,
  deadlineMeta,         // { color, label }
}) {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));

  // show fewer chips on smaller widths
  const maxVisible = lgUp ? 5 : mdUp ? 4 : 2;
  const [anchorEl, setAnchorEl] = useState(null);

  const chipSx = {
    height: 24,
    borderRadius: 1.5,
    '& .MuiChip-label': { px: 1, fontSize: 12, fontWeight: 600 },
  };

  const all = [
    contentType && <Chip key="ct" size="small" label={contentType} color="info" sx={chipSx} />,
    criticalityLevel && (
      <Chip
        key="crit"
        size="small"
        label={`Criticality: ${criticalityLevel}`}
        color={criticalityColor(criticalityLevel)}
        sx={chipSx}
      />
    ),
    updateNumber && (
      <Chip
        key="upd"
        size="small"
        icon={<NumbersIcon />}
        label={`Update # ${updateNumber}`}
        variant="outlined"
        sx={chipSx}
      />
    ),
    <Chip
      key="iss"
      size="small"
      icon={<CalendarTodayIcon />}
      label={`Issued: ${formatDate(issueDate)}`}
      variant="outlined"
      sx={chipSx}
    />,
    complianceDeadline && (
      <Chip
        key="dl"
        size="small"
        icon={<CalendarTodayIcon />}
        label={`Deadline: ${formatDate(complianceDeadline)}`}
        variant="outlined"
        sx={chipSx}
      />
    ),
    deadlineMeta && (
      <Chip
        key="meta"
        size="small"
        color={deadlineMeta.color}
        label={deadlineMeta.label}
        sx={chipSx}
      />
    ),
  ].filter(Boolean);

  const visible = all.slice(0, maxVisible);
  const hidden = all.slice(maxVisible);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1, alignItems: 'center' }}>
      {visible.map((c, i) => (
        <Box key={i} sx={{ display: 'inline-flex' }}>{c}</Box>
      ))}

      {hidden.length > 0 && (
        <>
          <Chip
            size="small"
            label={`+${hidden.length} more`}
            variant="outlined"
            sx={chipSx}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          />
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {hidden.map((c, i) => (
                <Box key={i}>{c}</Box>
              ))}
            </Box>
          </Menu>
        </>
      )}
    </Box>
  );
}
